import React, { useCallback, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Bounce, toast } from "react-toastify"
import dayjs from "dayjs"
import { useSearchParams } from "react-router-dom"
import ListingTabs from "../../../components/Listing/ListingTabs"
import SearchInput from "../../../components/Listing/SearchInput"
import FilterDropdown, {
  toSingleFilterValue,
} from "../../../components/Listing/FilterDropdown"
import ExportLogButton from "../../../components/Listing/ExportLogButton"
import Pagination from "../../../components/Listing/Pagination"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomIconButton from "../../../components/OnboardingComponents/CustomIconButton"
import EmptyState from "../../../components/Common/EmptyState"
import TruncatedText from "../../../components/Common/TruncatedText"
import DeleteConfirmation from "../../../components/Common/DeleteConfirmation"
import AdminCityListingTitle from "../../../components/AdminLayout/AdminCityListingTitle"
import { Avatar } from "../../../components/DashboardComponents/AvatarGroup"
import { ICONS } from "../../../constants/icons"
import { del, get, patch } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import useAdminPermission from "../../../hooks/useAdminPermission"
import {
  ADMIN_FLAG_DETAIL_WITH,
  ADMIN_FLAG_LIST_WITH,
  FLAG_STATUS_OPTIONS,
  FLAG_STATUS_TABS,
  STATUS_LABEL,
  isActionableFlagStatus,
  mapAdminFlagRow,
} from "./adminFlagsData"
import FlagDetailsModal from "./FlagDetailsModal"

const PAGE_SIZE = 10

const TOAST_OPTIONS = { transition: Bounce }

const DEFAULT_DISMISS_REASON =
  "After review, the reported content does not require further action."
const DEFAULT_RESOLVE_REASON =
  "After review, the reported issue has been resolved."

/**
 * Admin Flags & Reports — list / detail / patch / delete / export.
 * Optional cityId (prop or ?city_id=) scopes to a Manage City listing.
 */
const AdminFlags = ({
  documentTitle = "Flags & Reports | AMOK . EARTH Admin",
  cityId: cityIdProp,
}) => {
  const [searchParams] = useSearchParams()
  const { can } = useAdminPermission()
  const canUpdate = can("flags", "edit")
  const canDelete = can("flags", "delete")
  const cityId = cityIdProp || searchParams.get("city_id") || ""
  const [activeTab, setActiveTab] = useState("all")
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)

  const [rows, setRows] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState(null)

  const [selectedFlag, setSelectedFlag] = useState(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [isDetailLoading, setIsDetailLoading] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  const [flagToDelete, setFlagToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    document.title = documentTitle
  }, [documentTitle])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [activeTab, debouncedSearch, cityId])

  const buildListParams = useCallback(
    ({ includePage = true } = {}) => {
      const params = {
        "with[]": ADMIN_FLAG_LIST_WITH,
        sort_by: "created_at",
        sort_direction: "desc",
      }

      if (includePage) {
        params.page = currentPage
        params.page_size = pageSize
      }

      if (debouncedSearch) params.search = debouncedSearch

      // Tabs + status FilterDropdown share one status filter.
      if (activeTab !== "all") {
        params.status = activeTab
      }

      if (cityId) params.city_id = cityId

      return params
    },
    [activeTab, cityId, currentPage, pageSize, debouncedSearch],
  )

  const fetchFlags = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await get(endpoints.getAdminFlags, {
        params: buildListParams(),
      })

      if (response?.data?.success) {
        const result = response.data.result || {}
        const mapped = (result.data || []).map(mapAdminFlagRow)

        setRows(mapped)
        setTotalItems(result.total ?? mapped.length)
        setPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil(
            (result.total ?? mapped.length) / (result.per_page || PAGE_SIZE),
          ),
        )
        if (currentPage > totalPages) setCurrentPage(totalPages)
      } else {
        const message = toastApiError(response, "Failed to load flags")
        setError(message)
        setRows([])
        setTotalItems(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load flags")
      setError(message)
      setRows([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [buildListParams, currentPage])

  useEffect(() => {
    fetchFlags()
  }, [fetchFlags])

  const patchFlagStatus = useCallback(
    async (flag, status, decisionReason) => {
      if (!flag?.id || isUpdating) return false

      setIsUpdating(true)
      try {
        const response = await patch(
          endpoints.updateAdminFlag(flag.id),
          {
            status,
            decision_reason: decisionReason,
          },
          { skipErrorToast: true },
        )

        if (!response?.data?.success) {
          toastApiError(response, "Failed to update flag")
          return false
        }

        const mapped = mapAdminFlagRow(
          response.data.result || {
            ...(flag.raw || {}),
            status,
            decision_reason: decisionReason,
          },
        )

        setRows(prev => prev.map(row => (row.id === flag.id ? mapped : row)))
        setSelectedFlag(prev =>
          prev?.id === flag.id ? { ...prev, ...mapped } : prev,
        )
        return true
      } catch (err) {
        toastApiError(err, "Failed to update flag")
        return false
      } finally {
        setIsUpdating(false)
      }
    },
    [isUpdating],
  )

  const openDetails = async row => {
    if (!row?.id) return
    setSelectedFlag(row)
    setDetailsOpen(true)
    setIsDetailLoading(true)

    try {
      const response = await get(endpoints.getAdminFlagById(row.id), {
        params: { "with[]": ADMIN_FLAG_DETAIL_WITH },
      })

      if (response?.data?.success) {
        const mapped = mapAdminFlagRow(response.data.result || {})
        setSelectedFlag(mapped)
        setRows(prev =>
          prev.map(item =>
            item.id === mapped.id ? { ...item, ...mapped } : item,
          ),
        )
      } else {
        toastApiError(response, "Failed to load flag details")
      }
    } catch (err) {
      toastApiError(err, "Failed to load flag details")
    } finally {
      setIsDetailLoading(false)
    }
  }

  const closeDetails = () => {
    if (isUpdating) return
    setDetailsOpen(false)
    setSelectedFlag(null)
  }

  const handleResolve = async row => {
    const ok = await patchFlagStatus(row, "resolved", DEFAULT_RESOLVE_REASON)
    if (ok) toast.success("Flag resolved.", TOAST_OPTIONS)
  }

  const handleDismiss = async row => {
    const ok = await patchFlagStatus(row, "dismissed", DEFAULT_DISMISS_REASON)
    if (ok) toast.success("Flag dismissed.", TOAST_OPTIONS)
  }

  const handleDismissFromModal = async row => {
    const ok = await patchFlagStatus(row, "dismissed", DEFAULT_DISMISS_REASON)
    if (ok) {
      toast.success("Flag dismissed.", TOAST_OPTIONS)
      closeDetails()
    }
  }

  const handleRemoveContent = async row => {
    const ok = await patchFlagStatus(
      row,
      "resolved",
      "Content removed after review of the reported material.",
    )
    if (ok) {
      toast.success("Content removed and flag resolved.", TOAST_OPTIONS)
      closeDetails()
    }
  }

  const handleDeleteConfirm = async () => {
    if (!flagToDelete?.id || isDeleting) return

    setIsDeleting(true)
    try {
      const response = await del(endpoints.deleteAdminFlag(flagToDelete.id), {
        skipErrorToast: true,
      })

      if (
        !response ||
        response.status >= 400 ||
        response?.data?.success === false
      ) {
        toastApiError(response, "Failed to delete flag")
        return
      }

      toast.success(response?.data?.message || "Flag deleted.", TOAST_OPTIONS)
      setFlagToDelete(null)
      if (selectedFlag?.id === flagToDelete.id) closeDetails()
      await fetchFlags()
    } catch (err) {
      toastApiError(err, "Failed to delete flag")
    } finally {
      setIsDeleting(false)
    }
  }

  const handleExport = async () => {
    if (isExporting) return

    setIsExporting(true)
    try {
      const response = await get(endpoints.exportAdminFlags, {
        params: buildListParams({ includePage: false }),
        responseType: "blob",
        skipErrorToast: true,
      })

      if (!response || response.status >= 400) {
        toastApiError(response, "Failed to export flags")
        return
      }

      const contentType = String(
        response.headers?.["content-type"] || "",
      ).toLowerCase()

      if (contentType.includes("application/json")) {
        const text = await response.data.text()
        let message = "Failed to export flags"
        try {
          const parsed = JSON.parse(text)
          message = parsed?.message || message
        } catch {
          // keep default
        }
        toast.error(message, TOAST_OPTIONS)
        return
      }

      const disposition = response.headers?.["content-disposition"] || ""
      const filenameMatch = disposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i,
      )
      const filename = filenameMatch?.[1]
        ? filenameMatch[1].replace(/['"]/g, "")
        : `flags-${dayjs().format("YYYY-MM-DD")}.csv`

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)

      toast.success("Flags exported.", TOAST_OPTIONS)
    } catch (err) {
      toastApiError(err, "Failed to export flags")
    } finally {
      setIsExporting(false)
    }
  }

  const handleStatusFilterChange = next => {
    const value = toSingleFilterValue(next)
    setActiveTab(value || "all")
  }

  const skeletonRows = useMemo(
    () => Array.from({ length: 6 }, (_, index) => `skeleton-${index}`),
    [],
  )

  const safePage = Math.min(
    currentPage,
    Math.max(1, Math.ceil(totalItems / pageSize) || 1),
  )

  return (
    <div className="admin-page admin-flags">
      <div className="admin-flags__toolbar">
        {cityId ? (
          <AdminCityListingTitle label="Flags & Reports" cityId={cityId} />
        ) : (
          <h1 className="admin-flags__title">Flags & Reports</h1>
        )}
        <div className="admin-flags__controls">
          <SearchInput
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search by Content..."
            iconSrc={ICONS.callFormalSearchStatus}
            className="admin-flags__search"
          />
          <FilterDropdown
            label="Filter"
            filters={[
              {
                key: "status",
                type: "select",
                label: "Status",
                options: FLAG_STATUS_OPTIONS,
                value: activeTab === "all" ? "" : activeTab,
                defaultValue: "",
                onChange: handleStatusFilterChange,
              },
            ]}
          />
          <ExportLogButton
            entityLabel="flags"
            onClick={handleExport}
            loading={isExporting}
          />
        </div>
      </div>

      <div className="admin-flags__tabs-row">
        <ListingTabs
          items={FLAG_STATUS_TABS}
          activeKey={activeTab}
          onChange={setActiveTab}
        />
      </div>

      <div className="admin-flags__table-wrapper" aria-busy={isLoading}>
        <div className="admin-flags__table" role="table">
          <div
            className="admin-flags__table-row admin-flags__table-row--head"
            role="row"
          >
            <div role="columnheader">Reported By</div>
            <div role="columnheader">City</div>
            <div role="columnheader">Flagged Content</div>
            <div role="columnheader">Reason</div>
            <div role="columnheader">Date Reported</div>
            <div role="columnheader">Status</div>
            <div role="columnheader" className="admin-flags__actions-head">
              Actions
            </div>
          </div>

          {isLoading
            ? skeletonRows.map(key => (
                <div
                  key={key}
                  className="admin-flags__table-row admin-flags__table-row--skeleton"
                  role="row"
                >
                  <div className="admin-flags__skeleton" />
                  <div className="admin-flags__skeleton" />
                  <div className="admin-flags__skeleton" />
                  <div className="admin-flags__skeleton" />
                  <div className="admin-flags__skeleton" />
                  <div className="admin-flags__skeleton" />
                  <div className="admin-flags__skeleton" />
                </div>
              ))
            : rows.map(row => (
                <div key={row.id} className="admin-flags__table-row" role="row">
                  <div className="admin-flags__user" role="cell">
                    <Avatar
                      src={row.reporterAvatar}
                      name={row.reporter}
                      size="sm"
                      className="admin-flags__avatar"
                    />
                    <TruncatedText className="admin-flags__name">
                      {row.reporter}
                    </TruncatedText>
                  </div>
                  <TruncatedText
                    as="div"
                    className="admin-flags__city-cell"
                    role="cell"
                  >
                    {row.cityLabel || row.city}
                  </TruncatedText>
                  <TruncatedText
                    as="div"
                    className="admin-flags__cell"
                    role="cell"
                  >
                    {row.content}
                  </TruncatedText>
                  <TruncatedText
                    as="div"
                    className="admin-flags__cell"
                    role="cell"
                  >
                    {row.reason}
                  </TruncatedText>
                  <TruncatedText
                    as="div"
                    className="admin-flags__cell admin-flags__cell--center"
                    role="cell"
                  >
                    {row.date}
                  </TruncatedText>
                  <div className="admin-flags__status-cell" role="cell">
                    <span
                      className={`admin-flags__status admin-flags__status--${row.statusTone}`}
                    >
                      {STATUS_LABEL[row.status] || row.status}
                    </span>
                  </div>
                  <div className="admin-flags__actions" role="cell">
                    {canUpdate && isActionableFlagStatus(row.status) && (
                      <>
                        <CustomButton
                          variant="transparent"
                          size="sm"
                          type="button"
                          className="admin-flags__resolve-btn"
                          disabled={isUpdating}
                          onClick={() => handleResolve(row)}
                        >
                          Resolve
                        </CustomButton>
                        <CustomButton
                          variant="transparent"
                          size="sm"
                          type="button"
                          className="admin-flags__dismiss-btn"
                          disabled={isUpdating}
                          onClick={() => handleDismiss(row)}
                        >
                          Dismiss
                        </CustomButton>
                      </>
                    )}
                    <CustomIconButton
                      iconSrc={ICONS.adminEye}
                      label={`View flag from ${row.reporter}`}
                      size="sm"
                      className="admin-flags__eye-btn"
                      onClick={() => openDetails(row)}
                    />
                    {canDelete ? (
                      <CustomIconButton
                        iconSrc={ICONS.trash}
                        label={`Delete flag from ${row.reporter}`}
                        tone="danger"
                        size="sm"
                        onClick={() => setFlagToDelete(row)}
                      />
                    ) : null}
                  </div>
                </div>
              ))}

          {!isLoading && rows.length === 0 && (
            <div className="admin-flags__empty">
              <EmptyState
                title={error ? "Couldn’t load flags" : "No flags found"}
                description={error || "Try adjusting your search or filters."}
              />
            </div>
          )}
        </div>
      </div>

      <Pagination
        currentPage={safePage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        itemLabel="Flags"
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
        className="admin-flags__pagination"
      />

      <FlagDetailsModal
        isOpen={detailsOpen}
        onClose={closeDetails}
        flag={selectedFlag}
        isLoading={isDetailLoading}
        isUpdating={isUpdating}
        onDismiss={handleDismissFromModal}
        onRemoveContent={handleRemoveContent}
        onDelete={canDelete ? row => setFlagToDelete(row) : undefined}
        canUpdate={canUpdate}
        canDelete={canDelete}
      />

      <DeleteConfirmation
        isOpen={Boolean(flagToDelete)}
        onCancel={() => !isDeleting && setFlagToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete flag"
        message="Are you sure you want to delete this flag? This action cannot be undone."
        cancelText="Cancel"
        confirmText="Delete"
        isDeleting={isDeleting}
      />
    </div>
  )
}

export default AdminFlags

AdminFlags.propTypes = {
  documentTitle: PropTypes.string,
  cityId: PropTypes.string,
}

AdminFlags.defaultProps = {
  documentTitle: "Flags & Reports | AMOK . EARTH Admin",
  cityId: undefined,
}
